import type {CreateExtArgs, Extension, ExtensionAPI} from '@wix/document-manager-core'
import {uploadTimeoutReason} from './constants'

export enum PayloadStatus {
    COMPLETED = 'COMPLETED',
    RUNNING = 'RUNNING',
    FAILED = 'FAILED'
}

export enum HandlerStatus {
    COMPLETED = 'COMPLETED',
    RUNNING = 'RUNNING',
    FAILED = 'FAILED'
}

export interface PayloadData {
    status: PayloadStatus
    output: any
}

export interface Payload {
    id: string
    data: PayloadData
}

export interface HandlerResult {
    status: HandlerStatus
    reason?: string
}

export type Handler = (data: any) => Promise<HandlerResult>

export const DEFAULT_HANDLER_TIMEOUT_MS = 60000

export interface EventHandlerApi {
    registerExecutionIdToHandler(executionId: string, handler: Handler, caller?: string, timeoutMs?: number): void
    invokeExecutionHandler(payload: Payload): Promise<void>
}

export type EventHandlerExtApi = ExtensionAPI & {eventHandler: EventHandlerApi}

interface HandlerEntry {
    handler: Handler
    caller?: string
}

const createExtension = (): Extension => {
    const executionIdToHandlerMap: Record<string, HandlerEntry> = {}

    const createExtensionAPI = ({coreConfig}: CreateExtArgs): EventHandlerExtApi => {
        const {logger} = coreConfig
        const removeExecutionIdToHandler = (executionId: string) => {
            delete executionIdToHandlerMap[executionId]
        }

        const getExecutionIdToHandler = (executionId: string) => {
            return executionIdToHandlerMap[executionId]
        }

        const invokeExecutionHandler = async (payload: Payload) => {
            const {id: executionId, data} = payload ?? {}

            const entry = getExecutionIdToHandler(executionId)
            const handler = entry?.handler
            const caller = entry?.caller

            logger.interactionStarted(`handler_execution`, {
                extras: {
                    executionId,
                    caller,
                    payload,
                    handlerExists: !!handler
                }
            })

            try {
                const result = await handler?.(data)
                const {reason} = result ?? {}

                if (result.status === 'FAILED') {
                    const failureReason = reason ?? data?.output?.reason
                    const isTimeout = failureReason === uploadTimeoutReason
                    logger.captureError(new Error(isTimeout ? 'handler timeout' : 'handler failed'), {
                        extras: {executionId, caller, payload, reason}
                    })
                }

                logger.interactionEnded(`handler_execution`, {
                    extras: {
                        handlerResult: result,
                        executionId,
                        caller
                    }
                })

                if (result.status !== 'RUNNING') {
                    removeExecutionIdToHandler(executionId)
                }
            } catch (error) {
                logger.captureError(new Error('handler failed'), {
                    extras: {executionId, caller, payload, reason: error}
                })

                logger.interactionEnded(`handler_execution`, {
                    extras: {
                        handlerResult: {status: 'FAILED', reason: error},
                        executionId,
                        caller
                    }
                })

                removeExecutionIdToHandler(executionId)
            }
        }

        const registerExecutionIdToHandler = (
            executionId: string,
            handler: Handler,
            caller?: string,
            timeoutMs: number = DEFAULT_HANDLER_TIMEOUT_MS
        ) => {
            logger.interactionStarted(`handler_registered`, {
                extras: {executionId, caller}
            })
            executionIdToHandlerMap[executionId] = {handler, caller}

            setTimeout(() => {
                if (!getExecutionIdToHandler(executionId)) return
                logger.interactionEnded('handler timeout', {extras: {executionId, caller}})
                invokeExecutionHandler({
                    id: executionId,
                    data: {status: PayloadStatus.FAILED, output: {reason: uploadTimeoutReason}}
                })
            }, timeoutMs)

            logger.interactionEnded(`handler_registered`, {
                extras: {executionId, caller}
            })
        }

        return {
            eventHandler: {
                registerExecutionIdToHandler,
                invokeExecutionHandler
            }
        }
    }

    return {
        name: 'eventHandler',
        createExtensionAPI
    }
}

export {createExtension}
